import { globalConfirm } from '../../../components/ConfirmDialog';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SortableTableHeader, useSortState, sortData } from '../../../components/SortableTableHeader';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { useExpenditureLabels, useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';
import DataLookupField from '../../../components/DataLookupField';
import {
  Plus, Edit, Trash2, Save, Search, Download, X,
  FileText, Users, ChevronRight, ChevronDown, Bell,
  Lock, Eye, Pencil, Settings, Package
} from 'lucide-react';

export function PermPillRow({ label, value, onChange }) {
  const options = [
    { v: 'no_access', icon: Lock, color: 'text-red-400', bg: 'bg-red-50 border-red-300 dark:bg-red-950/30', tip: 'No Access' },
    { v: 'read_only', icon: Eye, color: 'text-amber-500', bg: 'bg-amber-50 border-amber-300 dark:bg-amber-950/30', tip: 'Read Only' },
    { v: 'read_write', icon: Pencil, color: 'text-green-600', bg: 'bg-green-50 border-green-300 dark:bg-green-950/30', tip: 'Read & Write' },
  ];
  return (
    <div className="flex items-center gap-2 mb-1.5" role="radiogroup" aria-label={`${label} permission`}>
      <span className="w-28 text-[10px] capitalize truncate">{label}</span>
      <div className="flex gap-0.5">
        {options.map(o => {
          const Icon = o.icon;
          const active = value === o.v;
          return (
            <button key={o.v} type="button" role="radio" aria-checked={active} aria-label={o.tip}
              title={o.tip}
              onClick={() => onChange(o.v)}
              className={`flex items-center gap-1 px-2 py-1 rounded-md border text-[10px] font-medium transition-all
                ${active ? `${o.bg} ${o.color} border-current shadow-sm` : 'border-transparent text-slate-400 hover:text-slate-600 hover:bg-muted/50'}`}
            >
              <Icon className="h-3 w-3" />
              {active && <span>{o.tip}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function RolesPanel() {
  const [permRoles, setPermRoles] = useState([]);
  const [roleSort, setRoleSort] = useState({ field: 'name', dir: 'asc' });
  const hRole = (f, d) => setRoleSort({ field: f, dir: d });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editType, setEditType] = useState('permission');

  useEffect(() => { fetchRoles(); }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const p = await api.get('/api/admin/permission-roles');
      setPermRoles(p.data.roles || []);
    } catch {}
    setLoading(false);
  };

  const deleteRole = async (type, id) => {
    if (!await globalConfirm('Delete this role?')) return;
    try {
      await api.delete(`/api/admin/${type}-roles/${id}`);
      toast.success('Role deleted');
      fetchRoles();
    } catch (err) { toast.error(err.response?.data?.detail || 'Cannot delete role'); }
  };

  // Permission grid fields
  const PERM_DATA_FILES = ['category_code','supplier_contracts','currency_code','delivery_point','invoice_point','items','document_type','terms_conditions','supplier','customers'];
  const PERM_ACCT_CODES = ['ac_account','ac_restaurant','ac_department_code','ac_gl','ac_property_code','ac_nominal_code','ac_cost_centre'];
  const PERM_FUNCTIONS = ['fn_requisitions','fn_purchase_orders','fn_sales_orders','fn_rfx','fn_asset','fn_invoice_payments','fn_inventory','fn_expense','fn_recipe','fn_data_log','fn_archive_orders','fn_export'];
  const PERM_OPTIONS = [{v:'read_write',l:'Read & Write'},{v:'read_only',l:'Read Only'},{v:'no_access',l:'No Access'}];

  if (editing) {
    const isNew = editing === 'new-perm' || editing === 'new-appr';
    const type = editType;
    return <RoleEditor role={isNew ? null : editing} type={type} isNew={isNew}
      permFields={{PERM_DATA_FILES, PERM_ACCT_CODES, PERM_FUNCTIONS, PERM_OPTIONS}}
      onClose={() => { setEditing(null); fetchRoles(); }} />;
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div data-testid="roles-panel">
      <h1 className="text-2xl font-bold mb-1">Roles</h1>
      <p className="text-lg font-medium text-muted-foreground mb-4">Permission Roles</p>

      <div className="flex justify-end mb-3">
        <Button size="sm" onClick={() => { setEditType('permission'); setEditing('new-perm'); }}><Plus className="h-4 w-4 mr-1" /> Add Permission Role</Button>
      </div>
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50"><tr><SortableTableHeader label="Role Name" field="name" sortState={roleSort} onSort={hRole} /><SortableTableHeader label="Description" field="description" sortState={roleSort} onSort={hRole} /><SortableTableHeader label="Members" field="member_count" sortState={roleSort} onSort={hRole} className="text-center" /><th className="w-20"></th></tr></thead>
          <tbody>
            {sortData(permRoles, roleSort.field, roleSort.dir).map(r => (
              <tr key={r.id} className="border-t hover:bg-accent/50 cursor-pointer" onClick={() => { setEditType('permission'); setEditing(r); }}>
                <td className="px-3 py-2 font-semibold text-primary">{r.name}</td>
                <td className="px-3 py-2 text-muted-foreground text-xs">{r.description}</td>
                <td className="px-3 py-2 text-center"><Badge variant="secondary">{r.member_count || 0}</Badge></td>
                <td className="px-2"><Button size="sm" variant="ghost" className="text-destructive" onClick={e => { e.stopPropagation(); deleteRole('permission', r.id); }}><Trash2 className="h-3 w-3" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function RoleEditor({ role, type, isNew, permFields, onClose }) {
  const [form, setForm] = useState(isNew ? { name: '', description: '', permissions: {}, approver_settings: {} } : { ...role });
  const [saving, setSaving] = useState(false);
  const [assigned, setAssigned] = useState([]);
  const [available, setAvailable] = useState([]);
  const [addingUser, setAddingUser] = useState('');
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const fp = (k, v) => setForm(p => ({ ...p, permissions: { ...p.permissions, [k]: v } }));
  const fa = (k, v) => setForm(p => ({ ...p, approver_settings: { ...p.approver_settings, [k]: v } }));

  // Load members for existing roles
  useEffect(() => {
    if (!isNew && role?.id) loadMembers();
  }, [role?.id]);

  const loadMembers = async () => {
    try {
      const r = await api.get(`/api/admin/${type}-roles/${role.id}/available-users`);
      setAssigned(r.data.assigned || []);
      setAvailable(r.data.available || []);
    } catch {}
  };

  const assignUser = async (userId) => {
    try {
      await api.post(`/api/admin/${type}-roles/${role.id}/assign/${userId}`);
      toast.success('User assigned');
      loadMembers();
      setAddingUser('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const unassignUser = async (userId) => {
    try {
      await api.post(`/api/admin/${type}-roles/${role.id}/unassign/${userId}`);
      toast.success('User removed from role');
      loadMembers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Role name required'); return; }
    setSaving(true);
    try {
      const endpoint = `/api/admin/${type}-roles`;
      if (isNew) await api.post(endpoint, form);
      else await api.put(`${endpoint}/${role.id}`, form);
      toast.success(isNew ? 'Role created' : 'Role updated');
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const { PERM_DATA_FILES, PERM_ACCT_CODES, PERM_FUNCTIONS, PERM_OPTIONS } = permFields;

  return (
    <div data-testid="role-editor">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold">{isNew ? 'Create' : 'Edit'} {type === 'permission' ? 'Permission' : 'Approval'} Role</h1>
          {!isNew && <p className="text-sm text-muted-foreground">{role.name}</p>}
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4 mr-1" /> Back</Button>
      </div>

      <div className="border rounded-lg p-6 mb-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label className="text-xs">Role Name *</Label>
            <Input value={form.name} onChange={e => f('name', e.target.value)} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Description</Label>
            <Input value={form.description} onChange={e => f('description', e.target.value)} /></div>
        </div>

        {type === 'permission' && (
          <div className="border-t pt-4">
            <h3 className="font-semibold text-sm mb-2">Permissions</h3>
            <div className="flex items-center gap-6 mb-4 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1"><Lock className="h-3 w-3 text-red-400" /> No Access</span>
              <span className="flex items-center gap-1"><Eye className="h-3 w-3 text-amber-500" /> Read Only</span>
              <span className="flex items-center gap-1"><Pencil className="h-3 w-3 text-green-500" /> Read & Write</span>
            </div>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <h4 className="text-xs font-semibold mb-2 text-muted-foreground">Data Files</h4>
                {PERM_DATA_FILES.map(k => (
                  <PermPillRow key={k} label={k.replace(/_/g,' ')} value={form.permissions?.[k]||'read_write'} onChange={v => fp(k, v)} />
                ))}
              </div>
              <div>
                <h4 className="text-xs font-semibold mb-2 text-muted-foreground">Account Codes</h4>
                {PERM_ACCT_CODES.map(k => (
                  <PermPillRow key={k} label={k.replace('ac_','').replace(/_/g,' ')} value={form.permissions?.[k]||'read_write'} onChange={v => fp(k, v)} />
                ))}
              </div>
              <div>
                <h4 className="text-xs font-semibold mb-2 text-muted-foreground">Functions</h4>
                {PERM_FUNCTIONS.map(k => {
                  const FUNC_LABELS = { fn_rfx: 'RFx Sourcing', fn_requisitions: 'Requisitions', fn_purchase_orders: 'Purchase Orders', fn_sales_orders: 'Sales Orders', fn_invoice_payments: 'Invoice & Payments', fn_archive_orders: 'Archive Orders', fn_data_log: 'Data Log' };
                  const label = FUNC_LABELS[k] || k.replace('fn_','').replace(/_/g,' ');
                  return <PermPillRow key={k} label={label} value={form.permissions?.[k]||'no_access'} onChange={v => fp(k, v)} />;
                })}
              </div>
            </div>
          </div>
        )}

        {type === 'approval' && (
          <div className="border-t pt-4">
            <h3 className="font-semibold text-sm mb-3">Approval Settings</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="border rounded p-3 space-y-3">
                <div className="flex items-center gap-2"><h4 className="text-xs font-semibold">Cost Centre Approver</h4>
                  <Switch checked={form.approver_settings?.cost_centre_approver} onCheckedChange={v => fa('cost_centre_approver', v)} /></div>
                {form.approver_settings?.cost_centre_approver && (
                  <div className="space-y-1"><Label className="text-[10px]">Limit Range</Label>
                    <div className="flex gap-1"><Input type="number" value={form.approver_settings?.cost_centre_limit_low||0} onChange={e => fa('cost_centre_limit_low', parseFloat(e.target.value)||0)} className="text-xs" />
                      <span className="pt-2 text-xs">—</span>
                      <Input type="number" value={form.approver_settings?.cost_centre_limit_high||0} onChange={e => fa('cost_centre_limit_high', parseFloat(e.target.value)||0)} className="text-xs" /></div></div>
                )}
              </div>
              <div className="border rounded p-3 space-y-3">
                <div className="flex items-center gap-2"><h4 className="text-xs font-semibold">Group Approver</h4>
                  <Switch checked={form.approver_settings?.group_approver} onCheckedChange={v => fa('group_approver', v)} /></div>
              </div>
              <div className="border rounded p-3 space-y-3">
                <div className="flex items-center gap-2"><h4 className="text-xs font-semibold">Rule Approver</h4>
                  <Switch checked={form.approver_settings?.rule_approver} onCheckedChange={v => fa('rule_approver', v)} /></div>
                {form.approver_settings?.rule_approver && (
                  <div className="space-y-1"><Label className="text-[10px]">Limit Range</Label>
                    <div className="flex gap-1"><Input type="number" value={form.approver_settings?.rule_limit_low||0} onChange={e => fa('rule_limit_low', parseFloat(e.target.value)||0)} className="text-xs" />
                      <span className="pt-2 text-xs">—</span>
                      <Input type="number" value={form.approver_settings?.rule_limit_high||0} onChange={e => fa('rule_limit_high', parseFloat(e.target.value)||0)} className="text-xs" /></div></div>
                )}
              </div>
              <div className="border rounded p-3 space-y-3">
                <div className="flex items-center gap-2"><h4 className="text-xs font-semibold">Invoice Approver</h4>
                  <Switch checked={form.approver_settings?.invoice_approver} onCheckedChange={v => fa('invoice_approver', v)} /></div>
                <div className="space-y-1"><Label className="text-[10px]">Expense Approver</Label>
                  <Input value={form.approver_settings?.expense_approver||''} onChange={e => fa('expense_approver', e.target.value)} className="text-xs" /></div>
                <div className="space-y-1"><Label className="text-[10px]">Request Approver</Label>
                  <Input value={form.approver_settings?.request_approver||''} onChange={e => fa('request_approver', e.target.value)} className="text-xs" /></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Members Management — only for existing roles */}
      {!isNew && role?.id && (
        <div className="border rounded-lg p-6 mb-4">
          <h3 className="font-semibold text-sm mb-3">Role Members ({assigned.length})</h3>

          {/* Add user to role */}
          <div className="flex items-center gap-2 mb-4">
            <select value={addingUser} onChange={e => setAddingUser(e.target.value)}
              className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm">
              <option value="">— Select a user to add —</option>
              {available.map(u => <option key={u.user_id} value={u.user_id}>{u.name} ({u.email})</option>)}
            </select>
            <Button size="sm" disabled={!addingUser} onClick={() => assignUser(addingUser)}>
              <Plus className="h-4 w-4 mr-1" /> Add to Role
            </Button>
          </div>

          {/* Current members list */}
          {assigned.length === 0 ? (
            <p className="text-sm text-center py-4">No users assigned to this role yet.</p>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr><th className="text-left px-3 py-2 font-medium">User</th><th className="text-left px-3 py-2 font-medium">Email</th><th className="w-10"></th></tr>
                </thead>
                <tbody>
                  {assigned.map(u => (
                    <tr key={u.user_id} className="border-t hover:bg-accent/30">
                      <td className="px-3 py-2 font-medium">{u.name}</td>
                      <td className="px-3 py-2 text-xs">{u.email}</td>
                      <td className="px-2">
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => unassignUser(u.user_id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button onClick={handleSave} disabled={saving}><Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save'}</Button>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
      </div>
    </div>
  );
}
